import { FC, useState } from 'react';
import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Divider, Stack, Tooltip } from '@mui/material';
import { APP, ROUTES } from 'config';
import {
  COLOR_BY_STATUS,
  SearchFilterName,
  adminOrdersFiltersSchema,
  paymentTypes,
  useAdminOrders,
  useBulkUpdate,
  useDownloadBulkUpdateTemplate,
  useExportToXls,
  useFilterValues,
} from 'store/admin-orders';
import { orderStatuses, OrderStatus } from 'store/orders';
import {
  AdminOrdersFilter,
  AdminShopUsersFilter,
  AdminSuppliersFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
  SelectFilter,
} from 'components/filters';
import moment from 'moment';
import { formatCurrency, formatList } from 'utils/format';
import { DownloadXlsAction } from 'components/actions';
import { useConfirm } from 'store/confirm';
import { SystemColors } from 'melp-design/style';
import { useSearchParams } from 'utils/navigation';
import { useModalContext } from 'store/modal';
import { OrdersBulkUpdateImportModal } from './atoms';

const VALID_FROM_STATUSES_BY_STATUS: Record<OrderStatus, OrderStatus[]> = {
  pending: [],
  in_progress: ['pending'],
  done: [],
  rejected: [],
  failed: [],
  invoiced: ['done', 'paid'],
  paid: ['done', 'invoiced'],
};

export const AdminOrdersList: FC = () => {
  const { t, i18n } = useTranslation();
  const { confirmation } = useConfirm();
  const { openModal } = useModalContext();

  const [filtersSearch, setFiltersSearch] = useState<
    Record<SearchFilterName, string>
  >({
    companyName: '',
    parentCompanyName: '',
    itemName: '',
    itemSku: '',
  });
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { searchParams: filters, navigate } = useSearchParams(
    adminOrdersFiltersSchema,
  );

  const { data: orders, isLoading: isOrdersLoading } = useAdminOrders(filters);
  const {
    data: filterValuesParentCompanies,
    isInitialLoading: isFilterValuesParentCompaniesLoading,
  } = useFilterValues('parentCompanyName', filtersSearch.parentCompanyName);
  const {
    data: filterValuesCompanies,
    isInitialLoading: isFilterValuesCompaniesLoading,
  } = useFilterValues('companyName', filtersSearch.companyName);
  const { data: filterValuesSku, isInitialLoading: isFilterValuesSkuLoading } =
    useFilterValues('itemSku', filtersSearch.itemSku);
  const {
    data: filterValuesItems,
    isInitialLoading: isFilterValuesItemsLoading,
  } = useFilterValues('itemName', filtersSearch.itemName);

  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();
  const { mutate: bulkUpdate, isLoading: isBulkUpdating } = useBulkUpdate();
  const { mutate: downloadTemplate, isLoading: isDownloadingTemplate } =
    useDownloadBulkUpdateTemplate();

  const onFiltersSearch = (filterName: SearchFilterName, search: string) => {
    setFiltersSearch((prev) => ({
      ...prev,
      [filterName]: search,
    }));
  };

  const getHint = (status: OrderStatus, createdAt: string) => {
    const SLA_WARNING = 18; // hours
    const SLA = 24; // hours

    const differenceInHours = moment().diff(createdAt, 'hours', true);
    const withinSLA = differenceInHours <= SLA;
    const withinSLAWarning = withinSLA && differenceInHours >= SLA_WARNING;
    const withSLARules = ['pending', 'in_progress'].includes(status);

    let hint = '';
    if (withSLARules) {
      if (withinSLAWarning) {
        hint = t('marketplaceOrders.SLAWarning', {
          SLAWaringThreshold: SLA_WARNING,
        });
      }
      if (!withinSLA) {
        hint = t('marketplaceOrders.SLAMissed', { SLAThreshold: SLA });
      }
    }

    if (status === 'failed') {
      hint = t('marketplaceOrders.orderFailed');
    }

    return hint;
  };

  const onBulkUpdate = async (status: OrderStatus) => {
    const selectedOrdersStatuses =
      orders?.items
        .filter((order) => Object.keys(selectedRowIds).includes(order.id))
        .map((order) => order.status) ?? [];
    if (
      selectedOrdersStatuses.some(
        (selectedOrdersStatus) =>
          !VALID_FROM_STATUSES_BY_STATUS[status].includes(selectedOrdersStatus),
      )
    ) {
      toast.error(
        t('marketplaceOrders.statusTransitionError', {
          allowedStatuses: formatList(
            i18n.language,
            VALID_FROM_STATUSES_BY_STATUS[status].map((status) =>
              t(`marketplaceOrders.status-${status}`),
            ),
          ),
          selectedStatus: t(`marketplaceOrders.status-${status}`),
          interpolation: { escapeValue: false },
        }),
      );
      return;
    }

    const { confirmed } = await confirmation(
      t('marketplaceOrders.statusUpdateConfirmation', {
        statusName: t(`marketplaceOrders.status-${status}`),
      }),
    );

    if (confirmed) {
      bulkUpdate(
        { ids: Object.keys(selectedRowIds), status },
        {
          onSuccess: () => {
            toast.success(t('common.completedSuccessfully'));
            setSelectedRowIds({});
          },
        },
      );
    }
  };

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('menu.orders'),
          },
        ]}
      />

      <PageContentLayout>
        <TableWrapper>
          <TableToolbar>
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
              >
                <ActionsMenu
                  type="spread"
                  items={[
                    {
                      label: t('global.actions.bulk_update'),
                      onClick: () => {
                        openModal({
                          component: OrdersBulkUpdateImportModal,
                        });
                      },
                    },
                    {
                      label: t('global.actions.export_template'),
                      onClick: () => {
                        downloadTemplate();
                      },
                      disabled: isDownloadingTemplate,
                    },
                  ]}
                />
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <ListTotal total={orders?.total} />

                <Stack direction="row" alignItems="center" gap={1}>
                  <Filters
                    value={filters}
                    initialValues={adminOrdersFiltersSchema.parse({})}
                    onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                    onChangeCallback={() => setSelectedRowIds({})}
                    showLabel={false}
                  >
                    {({ value, initialValues, setFilter }) => (
                      <>
                        <FilterButton
                          label={t('marketplaceOrders.orderDate')}
                          value={value.createdAt}
                          initialValue={initialValues.createdAt}
                          onChange={(v) => setFilter('createdAt', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <DateRangeFilter
                              value={value}
                              onApply={applyFilter}
                              onClear={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('expenses.expense_date')}
                          value={value.expenseDate}
                          initialValue={initialValues.expenseDate}
                          onChange={(v) => setFilter('expenseDate', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <DateRangeFilter
                              value={value}
                              onApply={applyFilter}
                              onClear={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('common.status')}
                          value={value.status}
                          initialValue={initialValues.status}
                          onChange={(v) => setFilter('status', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              name="status"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={orderStatuses.map((status) => ({
                                key: status,
                                label: t(`marketplaceOrders.status-${status}`),
                              }))}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('orders.paymentType.label')}
                          value={value.paymentType}
                          initialValue={initialValues.paymentType}
                          onChange={(v) => setFilter('paymentType', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              name="paymentType"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={paymentTypes.map((paymentType) => ({
                                key: paymentType,
                                label: t(`orders.paymentType.${paymentType}`),
                              }))}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('marketplaceOrders.orderId')}
                          value={value.orderIds}
                          initialValue={initialValues.orderIds}
                          onChange={(v) => setFilter('orderIds', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <AdminOrdersFilter
                              name="orderIds"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('entity_type.employee')}
                          value={value.shopUserIds}
                          initialValue={initialValues.shopUserIds}
                          onChange={(v) => setFilter('shopUserIds', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <AdminShopUsersFilter
                              optionKey="shopUserId"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('marketplaceOrders.client')}
                          value={value.parentCompanies}
                          initialValue={initialValues.parentCompanies}
                          onChange={(v) => setFilter('parentCompanies', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              name="parentCompanies"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={filterValuesParentCompanies?.map(
                                (o) => ({
                                  key: o,
                                  label: o,
                                }),
                              )}
                              loadingOptions={
                                isFilterValuesParentCompaniesLoading
                              }
                              asyncSearch
                              onSearch={(v) =>
                                onFiltersSearch('parentCompanyName', v)
                              }
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('marketplaceOrders.company')}
                          value={value.companies}
                          initialValue={initialValues.companies}
                          onChange={(v) => setFilter('companies', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              name="companies"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={filterValuesCompanies?.map((o) => ({
                                key: o,
                                label: o,
                              }))}
                              loadingOptions={isFilterValuesCompaniesLoading}
                              asyncSearch
                              onSearch={(v) =>
                                onFiltersSearch('companyName', v)
                              }
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('marketplaceItems.supplier')}
                          value={value.suppliers}
                          initialValue={initialValues.suppliers}
                          onChange={(v) => setFilter('suppliers', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <AdminSuppliersFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('marketplaceItems.sku')}
                          value={value.sku}
                          initialValue={initialValues.sku}
                          onChange={(v) => setFilter('sku', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              name="sku"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={filterValuesSku?.map((o) => ({
                                key: o,
                                label: o,
                              }))}
                              loadingOptions={isFilterValuesSkuLoading}
                              asyncSearch
                              onSearch={(v) => onFiltersSearch('itemSku', v)}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('marketplaceItems.name')}
                          value={value.items}
                          initialValue={initialValues.items}
                          onChange={(v) => setFilter('items', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              name="items"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={filterValuesItems?.map((o) => ({
                                key: o,
                                label: o,
                              }))}
                              loadingOptions={isFilterValuesItemsLoading}
                              asyncSearch
                              onSearch={(v) => onFiltersSearch('itemName', v)}
                            />
                          )}
                        </FilterButton>
                      </>
                    )}
                  </Filters>

                  <Divider orientation="vertical" variant="middle" flexItem />

                  <DownloadXlsAction
                    listLength={orders?.total ?? 0}
                    exportToExcel={() =>
                      exportToXls({
                        filters: {
                          ...filters,
                          orderIds: Object.keys(selectedRowIds),
                        },
                      })
                    }
                    loading={isExportingToXls}
                  />
                </Stack>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={orders?.items ?? []}
            isLoading={isOrdersLoading}
            stickyHeader
            rows={{
              color: ({ original: order }) => {
                if (
                  ['pending', 'in_progress'].includes(order.status) &&
                  moment().diff(order.createdAt, 'hours', true) <= 24
                ) {
                  return SystemColors.success.base + '25';
                }
                if (
                  ['pending', 'in_progress'].includes(order.status) &&
                  moment().diff(order.createdAt, 'hours', true) > 24
                ) {
                  return SystemColors.danger.base + '25';
                }
              },
            }}
            columns={[
              {
                id: 'date',
                header: t('common.date'),
                cell: ({ row: { original: order } }) => {
                  return (
                    <Stack gap={0.5}>
                      <Typography noWrap>
                        {`${t('marketplaceSuppliers.creationDate')}: ${moment(
                          order.createdAt,
                        ).format('l LT')}`}
                      </Typography>
                      <Typography color="textSecondary" noWrap>
                        {`${t('expenses.expense_date')}: ${
                          order.expenseDate
                            ? moment(order.expenseDate).format('l')
                            : '-'
                        }`}
                      </Typography>
                    </Stack>
                  );
                },
              },
              {
                id: 'status',
                header: t('common.status'),
                cell: ({ row: { original: order } }) => {
                  return (
                    <Tooltip title={getHint(order.status, order.createdAt)}>
                      <div>
                        <Typography color={COLOR_BY_STATUS[order.status]}>
                          {t(`marketplaceOrders.status-${order.status}`)}
                        </Typography>
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                id: 'orderNr',
                header: t('marketplaceOrders.orderId'),
                cell: ({ row: { original: order } }) => {
                  return (
                    <Link
                      label={order.orderNr}
                      to={ROUTES.admin.orders.details.replace(':id', order.id)}
                    />
                  );
                },
              },
              {
                id: 'parentCompany',
                header: t('marketplaceOrders.client'),
                cell: ({ row: { original: order } }) => {
                  return order.company.parentCompany ? (
                    <Link
                      label={order.company.parentCompany.name}
                      to={ROUTES.admin.clients.details.settings.replace(
                        ':id',
                        order.company.parentCompany.id,
                      )}
                    />
                  ) : null;
                },
              },
              {
                id: 'company',
                header: t('marketplaceOrders.company'),
                cell: ({ row: { original: order } }) => {
                  return (
                    <Stack>
                      {order.company.name}
                      {order.shopUser ? (
                        <Typography color="textSecondary">
                          {order.shopUser.fullName}
                        </Typography>
                      ) : null}
                    </Stack>
                  );
                },
              },
              {
                id: 'shopItem',
                header: t('marketplaceOrders.itemDetails'),
                cell: ({ row: { original: order } }) => {
                  return (
                    <Stack gap={0.5}>
                      <Link
                        label={order.shopItem.name}
                        to={ROUTES.admin.shopItems.details.settings.replace(
                          ':id',
                          order.shopItem.id,
                        )}
                      />
                      <Typography color="textSecondary">
                        {t('marketplace.employeeID', {
                          id: order.shopItem.sku,
                        })}
                      </Typography>
                    </Stack>
                  );
                },
              },
              {
                id: 'orderPrice',
                header: t('marketplaceOrders.marketplaceItemPrice'),
                cell: ({ row: { original: order } }) => {
                  return (
                    <Stack>
                      {formatCurrency(
                        i18n.language,
                        order.price,
                        order.currency,
                      )}
                      <Typography color="textSecondary">
                        {t(`orders.paymentType.${order.paymentType}`)}
                      </Typography>
                    </Stack>
                  );
                },
                meta: { align: 'right' },
              },
              {
                id: 'lastModifiedBy',
                header: t('marketplaceOrders.lastModifiedBy'),
                cell: ({ row: { original: order } }) => {
                  return order.isAutomatedVoucher ? (
                    t('orders.modified_by.automated_voucher')
                  ) : order.admin && order.company.parentCompany ? (
                    <Link
                      label={order.admin.fullName}
                      to={ROUTES.admin.clients.details.administrators.details
                        .replace(':clientId', order.company.parentCompany.id)
                        .replace(':adminId', order.admin.id)}
                    />
                  ) : null;
                },
              },
            ]}
            sort={{
              value: filters.sort,
              onChange: (v) => navigate({ params: { sort: v } }),
            }}
            pagination={
              orders && {
                count: orders.total,
                page: filters.page,
                pageSize: filters.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
            selection={{
              value: selectedRowIds,
              onChange: setSelectedRowIds,
              actions: orderStatuses
                .filter(
                  (status) => !!VALID_FROM_STATUSES_BY_STATUS[status].length,
                )
                .map((status) => ({
                  label: t('global.actions.mark_as', {
                    status: t(`marketplaceOrders.status-${status}`),
                  }),
                  onClick: () => {
                    onBulkUpdate(status);
                  },
                })),
              isLoading: isBulkUpdating,
            }}
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
